import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { Header } from '../components/Header';
import { Price } from '../components/Price';
import { Seo } from '../lib/seo';
import { supabase, type ProductPublic } from '../lib/supabase';
import { useProjectList } from '../lib/projectList';
import { discountForBrand, useCustomer } from '../lib/customer';
import { useAuth } from '../lib/auth';
import { versionedImageUrl } from '../lib/imageUrl';
import './Compose.css';

type ComposeProduct = ProductPublic & {
  product_type: 'compose_module' | 'compose_base';
  custom_fields: Record<string, string | number>;
};

type ComposeVariant = {
  id: string;
  product_id: string;
  full_sku: string;
  sku_suffix: string;
  attributes: Record<string, string | number>;
  msrp_usd: number;
  color_temperature_k: number | null;
  beam_angle_deg: number | null;
  finish: string | null;
  wattage_w: number | null;
  dim_method: string | null;
  primary_image_url: string | null;
};

type Slot = {
  productId: string;
  cct: number;
  finish: string;
  beam: string;
};

const SCALES = [25, 35, 45, 55] as const;
const POWER_GUIDE: Record<number, string> = {
  25: '2–5W',
  35: '3–7W',
  45: '5–10W',
  55: '7–15W',
};
const CONTROL_OPTIONS = [
  { value: 'ON/OFF', label: 'ON/OFF', note: 'Encendido y apagado' },
  { value: 'TRIAC', label: 'TRIAC', note: 'Atenuación por corte de fase' },
  { value: 'DALI', label: 'DALI', note: 'Control digital direccionable' },
];

const numberField = (p: ComposeProduct, key: string) => Number(p.custom_fields?.[key] ?? 0);
const textField = (p: ComposeProduct, key: string) => String(p.custom_fields?.[key] ?? '');
const imageOf = (p: ComposeProduct) => p.images?.find(i => i.is_thumbnail)?.url ?? p.images?.[0]?.url ?? '';
const imageForFinish = (p: ComposeProduct, finish: string) => {
  const original = imageOf(p);
  if (finish !== 'Negro' || !original.startsWith('/compose/') || original.endsWith('-black.png')) return versionedImageUrl(original);
  return versionedImageUrl(original.replace(/(?:-white)?\.(?:webp|png)$/, '-black.png'));
};
const productHref = (product: ComposeProduct, variant?: ComposeVariant | null) =>
  `/producto/${encodeURIComponent(product.sku)}${variant?.full_sku ? `?v=${encodeURIComponent(variant.full_sku)}` : ''}`;
const beamOf = (v: ComposeVariant) => v.beam_angle_deg == null ? 'WW' : String(v.beam_angle_deg);

function unique<T>(values: T[]) {
  return [...new Set(values)];
}

export function Compose() {
  const [products, setProducts] = useState<ComposeProduct[]>([]);
  const [variants, setVariants] = useState<ComposeVariant[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [scale, setScale] = useState(35);
  const [moduleCount, setModuleCount] = useState(3);
  const [mounting, setMounting] = useState<'trim' | 'trimless'>('trim');
  const [shape, setShape] = useState<'round' | 'square'>('square');
  const [baseFinish, setBaseFinish] = useState('Blanco');
  const [systemCct, setSystemCct] = useState(3000);
  const [control, setControl] = useState('ON/OFF');
  const [slots, setSlots] = useState<Slot[]>([]);
  const [systemQuantity, setSystemQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const { add } = useProjectList();
  const { session } = useAuth();
  const { customer } = useCustomer();
  const discount = discountForBrand(customer, 'nuled');
  const canViewDealerPrice = !!session && !!customer && discount > 0;
  const canViewPrices = !!session;

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const { data: productRows, error: productError } = await supabase
        .from('products_public')
        .select('*')
        .contains('category_paths', ['COMPOSE'])
        .order('sku');
      if (!alive) return;
      if (productError) {
        setLoadError(productError.message);
        setLoading(false);
        return;
      }
      const list = (productRows as ComposeProduct[]) ?? [];
      const ids = list.map(p => p.id);
      const { data: variantRows, error: variantError } = ids.length
        ? await supabase
          .from('product_variants')
          .select('id, product_id, full_sku, sku_suffix, attributes, msrp_usd, color_temperature_k, beam_angle_deg, finish, wattage_w, dim_method, primary_image_url')
          .in('product_id', ids)
          .eq('active', true)
          .limit(2000)
        : { data: [], error: null };
      if (!alive) return;
      if (variantError) setLoadError(variantError.message);
      setProducts(list);
      setVariants(((variantRows as ComposeVariant[]) ?? []).map(v => ({ ...v, msrp_usd: Number(v.msrp_usd) })));
      setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const modules = useMemo(
    () => products.filter(p => p.product_type === 'compose_module'),
    [products],
  );
  const bases = useMemo(
    () => products.filter(p => p.product_type === 'compose_base'),
    [products],
  );

  const eligibleModules = useMemo(() => modules.filter(p => {
    if (numberField(p, 'scale_mm') !== scale) return false;
    return variants.some(v => v.product_id === p.id && v.dim_method === control);
  }), [modules, variants, scale, control]);

  const variantsFor = (productId: string) => variants.filter(v => v.product_id === productId && v.dim_method === control);

  const defaultSlot = (product?: ComposeProduct, preferred?: Partial<Slot>): Slot => {
    const chosenProduct = product ?? eligibleModules[0];
    const available = chosenProduct ? variantsFor(chosenProduct.id) : [];
    const desiredCct = preferred?.cct ?? systemCct;
    const desiredFinish = preferred?.finish ?? baseFinish;
    const desiredBeam = preferred?.beam ?? '24';
    const first = available.find(v => v.color_temperature_k === desiredCct && v.finish === desiredFinish && beamOf(v) === desiredBeam)
      ?? available.find(v => v.color_temperature_k === desiredCct && v.finish === desiredFinish)
      ?? available.find(v => v.color_temperature_k === desiredCct)
      ?? available[0];
    return {
      productId: chosenProduct?.id ?? '',
      cct: first?.color_temperature_k ?? desiredCct,
      finish: first?.finish ?? desiredFinish,
      beam: first ? beamOf(first) : desiredBeam,
    };
  };

  useEffect(() => {
    if (!eligibleModules.length) {
      setSlots([]);
      return;
    }
    setSlots(current => Array.from({ length: moduleCount }, (_, index) => {
      const previous = current[index];
      const previousProduct = previous
        ? eligibleModules.find(product => product.id === previous.productId)
        : undefined;
      return previousProduct
        ? defaultSlot(previousProduct, { ...previous, cct: systemCct })
        : defaultSlot();
    }));
    // Conserva cada selección compatible al cambiar reglas globales del sistema.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, moduleCount, control, eligibleModules.length]);

  const matchingBases = bases.filter(p =>
    numberField(p, 'scale_mm') === scale
    && numberField(p, 'module_count') === moduleCount
    && textField(p, 'mounting') === mounting
    && textField(p, 'shape') === shape,
  );
  const selectedBase = matchingBases[0] ?? null;
  const selectedBaseVariant = selectedBase
    ? variants.find(v => v.product_id === selectedBase.id && v.finish === baseFinish) ?? null
    : null;

  const selectedModuleVariants = slots.map(slot => variantsFor(slot.productId).find(v =>
    v.color_temperature_k === slot.cct
    && v.finish === slot.finish
    && beamOf(v) === slot.beam,
  ) ?? null);

  const totalWatts = selectedModuleVariants.reduce((sum, v) => sum + Number(v?.wattage_w ?? 0), 0);
  const totalUsd = Number(selectedBaseVariant?.msrp_usd ?? 0)
    + selectedModuleVariants.reduce((sum, v) => sum + Number(v?.msrp_usd ?? 0), 0);
  const complete = !!selectedBaseVariant
    && slots.length === moduleCount
    && selectedModuleVariants.every(Boolean);
  const reference = `COMPOSE-${scale}-${moduleCount}M-${systemCct === 2700 ? '27' : '30'}-${control === 'ON/OFF' ? 'ON' : control}`;

  const updateSlot = (index: number, patch: Partial<Slot>) => {
    setSlots(current => current.map((slot, i) => i === index ? { ...slot, ...patch } : slot));
  };

  const selectModule = (index: number, productId: string) => {
    const product = eligibleModules.find(p => p.id === productId);
    setSlots(current => current.map((slot, i) => i === index
      ? defaultSlot(product, { ...slot, productId, cct: systemCct })
      : slot));
  };

  const chooseBaseFinish = (finish: string) => {
    setBaseFinish(finish);
    setSlots(current => current.map(slot => ({ ...slot, finish })));
  };

  const chooseSystemCct = (cct: number) => {
    setSystemCct(cct);
    setSlots(current => current.map(slot => ({ ...slot, cct })));
  };

  const chooseScale = (next: number) => {
    setScale(next);
    if (next === 55 && moduleCount === 3) setModuleCount(2);
  };

  const chooseModules = (count: number) => {
    setModuleCount(count);
    if (count > 1) setShape('square');
    if (count === 3) setMounting('trim');
  };

  const addSystem = () => {
    if (!session || !complete || !selectedBase || !selectedBaseVariant) return;
    const net = (n: number) => canViewDealerPrice ? n * (1 - discount / 100) : undefined;
    add({
      product_id: selectedBase.id,
      sku: selectedBaseVariant.full_sku,
      name: selectedBase.name,
      brand_code: 'nuled',
      msrp_usd: selectedBaseVariant.msrp_usd,
      cost_usd: net(selectedBaseVariant.msrp_usd),
      image_url: imageForFinish(selectedBase, baseFinish),
      notes: `${reference} · Base ${baseFinish}`,
      qty: systemQuantity,
    });
    selectedModuleVariants.forEach((variant, index) => {
      if (!variant) return;
      const product = modules.find(p => p.id === variant.product_id)!;
      add({
        product_id: product.id,
        sku: variant.full_sku,
        name: product.name,
        brand_code: 'nuled',
        msrp_usd: variant.msrp_usd,
        cost_usd: net(variant.msrp_usd),
        image_url: imageForFinish(product, slots[index]?.finish ?? variant.finish ?? 'Blanco'),
        notes: `${reference} · Posición ${index + 1} · ${control}`,
        qty: systemQuantity,
      });
    });
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1800);
  };

  if (loading) return <ComposeShell><div className="compose-loading">Preparando el sistema modular…</div></ComposeShell>;
  if (loadError) return <ComposeShell><div className="compose-loading">No pudimos cargar COMPOSE: {loadError}</div></ComposeShell>;

  return (
    <ComposeShell>
      <section className="compose-hero">
        <div className="compose-hero-copy">
          <div className="compose-kicker">COMPOSE by NULED</div>
          <h1>Build your light.</h1>
          <p>Una plataforma de iluminación arquitectónica que se configura para cada proyecto.</p>
          <a href="#configurador" className="compose-primary-link">Configurar un sistema</a>
        </div>
        <div className="compose-hero-products" aria-label="Módulos originales de la línea COMPOSE">
          {['/compose/a.png', '/compose/gd.png', '/compose/ww.png'].map((src, i) => (
            <img key={src} src={versionedImageUrl(src)} alt={['Downlight dirigible', 'Módulo GD', 'Wallwasher'][i]} />
          ))}
        </div>
      </section>

      <section className="compose-explainer">
        <div>
          <div className="compose-kicker">Cómo funciona</div>
          <h2>Una base. Hasta tres funciones de luz.</h2>
        </div>
        <div className="compose-explainer-grid">
          <article><b>01</b><h3>Define la capacidad</h3><p>El diámetro indica la escala física y el rango de potencia: de 2–5W en Ø25 hasta 7–15W en Ø55.</p></article>
          <article><b>02</b><h3>Construye cada posición</h3><p>En una base múltiple, cada módulo puede ser distinto: proyector extraíble, wallwasher u otra familia compatible.</p></article>
          <article><b>03</b><h3>Unifica el control</h3><p>Todo el sistema usa una sola tecnología: ON/OFF, TRIAC o DALI. El acabado de la base se especifica aparte.</p></article>
        </div>
      </section>

      <section className="compose-builder" id="configurador">
        <div className="compose-builder-heading">
          <div>
            <div className="compose-kicker">Configurador</div>
            <h2>Arma tu sistema.</h2>
          </div>
          <div className="made-to-project"><span>Made to Project</span><small>Fabricación especial · estimado 6–8 semanas</small></div>
        </div>

        <ConfiguratorStep number="01" title="Capacidad y potencia" description="Elige el diámetro según la cantidad de luz que requiere el proyecto.">
          <div className="scale-grid">
            {SCALES.map(value => (
              <button key={value} className={scale === value ? 'selected' : ''} onClick={() => chooseScale(value)}>
                <strong>Ø{value}</strong><span>{POWER_GUIDE[value]}</span><small>{value < 45 ? 'Aplicación compacta' : 'Mayor salida de luz'}</small>
              </button>
            ))}
          </div>
          <p className="technical-note">La potencia exacta se elige en cada módulo. Los lúmenes quedarán pendientes hasta recibir la información fotométrica del proveedor.</p>
        </ConfiguratorStep>

        <ConfiguratorStep number="02" title="Base e integración" description="Define cuántas posiciones tendrá el sistema y cómo se integra al plafón.">
          <ChoiceRow label="Módulos">
            {[1, 2, 3].map(n => <Choice key={n} selected={moduleCount === n} disabled={n === 3 && scale === 55} onClick={() => chooseModules(n)}>{n}</Choice>)}
          </ChoiceRow>
          <ChoiceRow label="Montaje">
            <Choice selected={mounting === 'trim'} onClick={() => setMounting('trim')}>Con trim</Choice>
            <Choice selected={mounting === 'trimless'} disabled={moduleCount === 3} onClick={() => setMounting('trimless')}>Trimless</Choice>
          </ChoiceRow>
          <ChoiceRow label="Forma">
            <Choice selected={shape === 'round'} disabled={moduleCount > 1} onClick={() => setShape('round')}>Redonda</Choice>
            <Choice selected={shape === 'square'} onClick={() => setShape('square')}>Cuadrada</Choice>
          </ChoiceRow>
          <ChoiceRow label="Color de la base">
            <Choice selected={baseFinish === 'Blanco'} onClick={() => chooseBaseFinish('Blanco')}><i className="swatch white" />Blanco</Choice>
            <Choice selected={baseFinish === 'Negro'} onClick={() => chooseBaseFinish('Negro')}><i className="swatch black" />Negro</Choice>
          </ChoiceRow>
          {mounting === 'trim' && <p className="technical-note">En montajes con trim, este color corresponde al marco visible en el plafón. También se aplica inicialmente a todos los módulos.</p>}
          {selectedBase ? (
            <div className="base-preview">
              <Link className="base-product-image" to={productHref(selectedBase, selectedBaseVariant)} title={`Ver ficha de ${selectedBase.name}`}><img src={imageForFinish(selectedBase, baseFinish)} alt={`${selectedBase.name} en ${baseFinish.toLowerCase()}`} /></Link>
              <div><small>BASE SELECCIONADA · {baseFinish.toUpperCase()}</small><Link className="product-name-link" to={productHref(selectedBase, selectedBaseVariant)}><strong>{selectedBase.name}</strong></Link><span>{String(selectedBase.specs?.dimensiones ?? '')} · Corte: {String(selectedBase.specs?.corte_plafon ?? '')}</span><code>{selectedBaseVariant?.full_sku}</code></div>
            </div>
          ) : <div className="compose-warning">Esta combinación de base no existe en la línea técnica disponible.</div>}
        </ConfiguratorStep>

        <ConfiguratorStep number="03" title="Temperatura y control" description="Estas decisiones aplican a todas las luminarias del sistema.">
          <ChoiceRow label="Temperatura">
            <Choice selected={systemCct === 2700} onClick={() => chooseSystemCct(2700)}>2700K</Choice>
            <Choice selected={systemCct === 3000} onClick={() => chooseSystemCct(3000)}>3000K</Choice>
          </ChoiceRow>
          <div className="control-grid">
            {CONTROL_OPTIONS.map(option => (
              <button key={option.value} className={control === option.value ? 'selected' : ''} onClick={() => setControl(option.value)}>
                <strong>{option.label}</strong><small>{option.note}</small>
              </button>
            ))}
          </div>
          <p className="technical-note">La temperatura y la atenuación se unifican para todo el sistema. La atenuación no modifica la base.</p>
        </ConfiguratorStep>

        <ConfiguratorStep number="04" title="Luz por posición" description="Cada espacio puede llevar una luminaria y una función distinta.">
          <div className="finish-availability"><span><i className="swatch white" /><i className="swatch black" /></span><p><strong>Acabado coordinado.</strong> Cada posición toma el color de la base; puedes cambiar una luminaria individualmente si el proyecto lo requiere.</p></div>
          <div className="slot-list">
            {slots.map((slot, index) => {
              const product = eligibleModules.find(p => p.id === slot.productId);
              const available = variantsFor(slot.productId);
              const finishes = unique(available.map(v => v.finish).filter((x): x is string => !!x));
              const beams = unique(available.map(beamOf));
              const exact = selectedModuleVariants[index];
              return (
                <article className="slot-card" key={index}>
                  <div className="slot-photo"><span>POSICIÓN {index + 1} · {slot.finish.toUpperCase()}</span>{product && <Link className="slot-photo-link" to={productHref(product, exact)} title={`Ver ficha de ${product.name}`}><img src={imageForFinish(product, slot.finish)} alt={`${product.name} en ${slot.finish.toLowerCase()}`} /></Link>}</div>
                  <div className="slot-fields">
                    <label>Familia y potencia<select value={slot.productId} onChange={e => selectModule(index, e.target.value)}>{eligibleModules.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
                    <label>Acabado<select value={slot.finish} onChange={e => updateSlot(index, { finish: e.target.value })}>{finishes.map(v => <option key={v}>{v}</option>)}</select></label>
                    <label>Apertura<select value={slot.beam} onChange={e => updateSlot(index, { beam: e.target.value })}>{beams.map(v => <option key={v} value={v}>{v === 'WW' ? 'Wallwasher' : `${v}°`}</option>)}</select></label>
                    <div className="slot-sku"><span>{exact?.wattage_w}W · {control}</span><div><code>{exact?.full_sku ?? 'Configuración no disponible'}</code>{product && exact && <Link to={productHref(product, exact)}>Ver ficha individual →</Link>}</div></div>
                  </div>
                </article>
              );
            })}
          </div>
          {!eligibleModules.length && <div className="compose-warning">No hay módulos compatibles con esta escala y control.</div>}
        </ConfiguratorStep>

        <section className="compose-summary">
          <div className="summary-title"><div><div className="compose-kicker">Tu configuración</div><h2>{reference}</h2></div><span className={complete ? 'complete' : ''}>{complete ? 'Sistema completo' : 'Falta configurar'}</span></div>
          <div className="summary-grid">
            <div className="summary-lines">
              {selectedBaseVariant && <SummaryLine href={productHref(selectedBase!, selectedBaseVariant)} image={imageForFinish(selectedBase!, baseFinish)} label={`Base · ${baseFinish}`} name={selectedBase!.name} sku={selectedBaseVariant.full_sku} price={canViewPrices ? selectedBaseVariant.msrp_usd : null} />}
              {selectedModuleVariants.map((variant, index) => {
                const product = variant ? modules.find(p => p.id === variant.product_id) : null;
                return variant && product ? <SummaryLine key={index} href={productHref(product, variant)} image={imageForFinish(product, slots[index]?.finish ?? variant.finish ?? 'Blanco')} label={`Posición ${index + 1} · ${slots[index]?.finish ?? variant.finish ?? 'Blanco'}`} name={product.name} sku={variant.full_sku} price={canViewPrices ? variant.msrp_usd : null} /> : null;
              })}
            </div>
            <aside className="summary-total">
              <dl><div><dt>Potencia total</dt><dd>{totalWatts}W</dd></div><div><dt>Flujo luminoso</dt><dd>Por confirmar</dd></div><div><dt>Temperatura</dt><dd>{systemCct}K</dd></div><div><dt>Control</dt><dd>{control}</dd></div><div><dt>Fabricación</dt><dd>6–8 semanas</dd></div></dl>
              {canViewPrices ? <div className="total-price"><small>Precio público del sistema</small><Price usd={totalUsd} size="lg" /></div> : <div className="price-locked"><span>Precios disponibles con sesión</span><a href="/login">Inicia sesión para consultar precios</a></div>}
              {canViewDealerPrice && <div className="dealer-price">Tu precio distribuidor: ${(totalUsd * (1 - discount / 100)).toFixed(2)} USD</div>}
              {session && <div className="system-quantity">
                <div><small>Cantidad</small><strong>Sistemas iguales</strong></div>
                <div className="quantity-stepper">
                  <button type="button" aria-label="Disminuir cantidad de sistemas" disabled={systemQuantity <= 1} onClick={() => setSystemQuantity(qty => Math.max(1, qty - 1))}>−</button>
                  <input aria-label="Cantidad de sistemas" type="number" min="1" max="99" inputMode="numeric" value={systemQuantity} onChange={e => setSystemQuantity(Math.min(99, Math.max(1, Math.trunc(Number(e.target.value)) || 1)))} />
                  <button type="button" aria-label="Aumentar cantidad de sistemas" disabled={systemQuantity >= 99} onClick={() => setSystemQuantity(qty => Math.min(99, qty + 1))}>+</button>
                </div>
              </div>}
              {session
                ? <button className="add-system" disabled={!complete} onClick={addSystem} aria-live="polite">{added ? `✓ ${systemQuantity === 1 ? 'Sistema agregado' : `${systemQuantity} sistemas agregados`}` : `Agregar ${systemQuantity === 1 ? 'sistema' : `${systemQuantity} sistemas`} a cotización`}</button>
                : <a className="add-system" href="/login">Inicia sesión para cotizar</a>}
              <p>Se agregan la base y cada módulo como partidas exactas para conservar SKUs, especificaciones y orden de compra.</p>
            </aside>
          </div>
        </section>
      </section>
    </ComposeShell>
  );
}

function ComposeShell({ children }: { children: React.ReactNode }) {
  return <div className="compose-experience"><Seo title="COMPOSE by NULED · Sistema de iluminación modular" description="Configura una luminaria arquitectónica modular por escala, potencia, base, función, óptica, CCT, acabado y control." canonical="https://nuled.mx/compose" /><Header visualNav variant="compose" /> <main className="compose-page">{children}</main><Footer /></div>;
}

function ConfiguratorStep({ number, title, description, children }: { number: string; title: string; description: string; children: React.ReactNode }) {
  return <section className="config-step"><header><span>{number}</span><div><h3>{title}</h3><p>{description}</p></div></header><div className="step-content">{children}</div></section>;
}

function ChoiceRow({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="choice-row"><span>{label}</span><div>{children}</div></div>;
}

function Choice({ selected, disabled, onClick, children }: { selected: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode }) {
  return <button disabled={disabled} className={selected ? 'selected' : ''} onClick={onClick}>{children}</button>;
}

function SummaryLine({ href, image, label, name, sku, price }: { href: string; image: string; label: string; name: string; sku: string; price: number | null }) {
  return <div className="summary-line"><Link className="summary-product-image" to={href}><img src={image} alt="" /></Link><div><small>{label}</small><Link className="product-name-link" to={href}><strong>{name}</strong></Link><code>{sku}</code></div>{price != null && <Price usd={price} size="sm" showSecondary={false} />}</div>;
}
